import { useEffect, useState } from "react";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import DetailsCard from "@/components/DetailsCard";
import Switch from "@/components/Switch";
import TextField from "@/components/TextField";
import type { UpsertTaskDepartmentNotificationDestinationInput } from "../mutations";
import type { TaskDepartmentNotificationDestination } from "../queries";

const INBOX_EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export type DestinationDraft = Pick<
  TaskDepartmentNotificationDestination,
  "workdayDepartmentName" | "email" | "emailEnabled" | "emailLastError"
> & { id?: string };

function isValidInboxEmail(value: string): boolean {
  return INBOX_EMAIL_REGEX.test(value.trim());
}

type DestinationEditorProps = {
  destination: DestinationDraft;
  saving: boolean;
  onSave: (input: UpsertTaskDepartmentNotificationDestinationInput) => Promise<boolean>;
};

export default function DestinationEditor({ destination, saving, onSave }: DestinationEditorProps) {
  const [email, setEmail] = useState(destination.email ?? "");
  const [emailEnabled, setEmailEnabled] = useState(destination.emailEnabled);
  const [error, setError] = useState<string | undefined>();
  const [toggleSaving, setToggleSaving] = useState(false);

  useEffect(() => {
    setEmail(destination.email ?? "");
    setEmailEnabled(destination.emailEnabled);
    setError(undefined);
  }, [destination.id, destination.email, destination.emailEnabled]);

  const storedEmail = destination.email ?? "";
  const departmentName = destination.workdayDepartmentName;
  const showInboxField = emailEnabled;
  const showInboxActions = showInboxField && email.trim() !== storedEmail.trim();
  const showStaleBanner = Boolean(destination.emailLastError) && emailEnabled;
  const controlsDisabled = saving || toggleSaving;

  async function persist(nextEnabled: boolean, nextEmail: string) {
    if (nextEnabled) {
      const trimmed = nextEmail.trim();
      if (!trimmed) {
        setError("Enter an email address.");
        return false;
      }
      if (!isValidInboxEmail(trimmed)) {
        setError("Enter a valid email.");
        return false;
      }
      setError(undefined);
      return onSave({
        workdayDepartmentName: departmentName,
        emailEnabled: true,
        email: trimmed
      });
    }

    setError(undefined);
    return onSave({
      workdayDepartmentName: departmentName,
      emailEnabled: false
    });
  }

  async function persistToggle(nextEnabled: boolean) {
    const previous = emailEnabled;
    setEmailEnabled(nextEnabled);
    if (nextEnabled && !storedEmail.trim()) {
      return;
    }

    setToggleSaving(true);
    try {
      const ok = await persist(nextEnabled, storedEmail);
      if (!ok) {
        setEmailEnabled(previous);
      }
    } catch {
      setEmailEnabled(previous);
    } finally {
      setToggleSaving(false);
    }
  }

  return (
    <section aria-label={`${departmentName} notification preferences`}>
      <DetailsCard heading={departmentName}>
        <div className="flex flex-col gap-6">
          {showStaleBanner ? (
            <Banner
              variant="error"
              hideSecondaryDismiss
              description="The last email to this inbox did not send."
              subText={destination.emailLastError ?? undefined}
            />
          ) : null}
          <Switch
            headline="Send Email"
            aria-label={`Send Email, ${departmentName}`}
            selected={emailEnabled}
            disabled={controlsDisabled}
            onChange={() => {
              void persistToggle(!emailEnabled);
            }}
          />
          {showInboxField ? (
            <>
              <TextField
                className="w-full"
                label={`Email Address, ${departmentName}`}
                type="email"
                value={email}
                disabled={controlsDisabled}
                error={Boolean(error)}
                errorText={error}
                onChangeText={(value) => {
                  setEmail(value);
                  if (error) {
                    setError(undefined);
                  }
                }}
              />
              {showInboxActions ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="filled"
                    loading={saving}
                    ariaLabel={`Save ${departmentName} inbox`}
                    onClick={() => {
                      void persist(true, email);
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="text"
                    disabled={controlsDisabled}
                    ariaLabel={`Cancel ${departmentName} inbox`}
                    onClick={() => {
                      setEmail(storedEmail);
                      setError(undefined);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </DetailsCard>
    </section>
  );
}
